import React, { useContext, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Text } from '../components/Texte';
import { SafeAreaInsetsContext, SafeAreaView } from 'react-native-safe-area-context';
import * as AppleAuthentication from 'expo-apple-authentication';
import { Eye, EyeOff } from '../components/icons';
import { BrandLogo } from '../components/BrandLogo';
import { FondNuit } from '../components/FondNuit';
import { useGabarit } from '../hooks/useGabarit';
import { GradientButton } from '../components/GradientButton';
import { GradientText } from '../components/GradientText';
import { Segmented } from '../components/Segmented';
import { TermsConsent } from '../components/TermsConsent';
import { GoogleLogo } from '../components/GoogleLogo';
import { useAuth } from '../hooks/useAuth';
import { useUserStore } from '../store/userStore';
import { isValidEmail, validatePassword } from '../utils/validators';
import { authErrorText } from '../utils/authErrors';
import { fleche } from '../i18n/sens';
import { colonneFormulaire, colors, formes, gradients, layout, screenPadding, spacing, typography } from '../theme/tokens';
import { NOM_MARQUE } from '../config/marque';
import { t } from '../i18n';

type Mode = 'signin' | 'signup';

// Le profil vient de l'URL (`?profil=`) posée par le menu « Créer son compte » du site : une valeur
// inconnue retombe sur le texte étudiant, qui est aussi celui d'une arrivée sans profil.
const COPIES_PROFIL = {
  etablissement: 'auth.signupEtablissement',
  partenaire: 'auth.signupPartenaire',
} as const;

export interface AuthScreenProps {
  profil?: string;
  // Mode d'arrivée selon le CTA de l'onboarding (« Créer mon compte » → signup ; sinon signin).
  initialMode?: Mode;
  // Retour à l'accueil. Absent quand l'écran est la première page de la pile (arrivée par l'URL
  // d'un écran protégé, sans session) : il n'y a alors nulle part où revenir, et pas de bouton.
  onBack?: () => void;
  onOpenTerms?: () => void;
  onOpenPrivacy?: () => void;
}

// Textes par mode (seuls les libellés changent ; le design est identique).
interface AuthCopy {
  title: string;
  titleAccent: string;
  subtitle: string;
  cta: string;
}

// Construits AU RENDU : constantes de module = libellés figés à l'import.
const texts = (): Record<Mode, AuthCopy> => ({
  signup: {
    title: t('auth.signup.title'),
    titleAccent: t('auth.signup.titleAccent'),
    subtitle: t('auth.signup.subtitle'),
    cta: t('auth.signup.cta'),
  },
  signin: {
    title: t('auth.signin.title'),
    titleAccent: t('auth.signin.titleAccent'),
    subtitle: t('auth.signin.subtitle'),
    cta: t('auth.signin.cta'),
  },
});

// Message neutre après une inscription sans erreur. Couvre à l'identique le nouveau compte et
// l'adresse déjà utilisée : il ne révèle jamais si l'email existe déjà (anti-énumération).
const signupPendingMessage = () => t('auth.pending');

// Hauteur de la bande sombre, derrière l'en-tête : celle de la maquette.
const HAUTEUR_BANDEAU = 360;

export function AuthScreen({ initialMode = 'signin', profil, onBack, onOpenTerms, onOpenPrivacy }: AuthScreenProps = {}) {
  const { signInEmail, signUpEmail, requestPasswordReset, signInWithGoogle, signInWithApple } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  // Vue « mot de passe oublié » : même écran, mêmes en-tête et feuille, autres champs et autre action.
  const [oubli, setOubli] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [champActif, setChampActif] = useState<'email' | 'password' | null>(null);
  const emailRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);

  // La feuille claire descend jusqu'au bord de l'écran : la zone sûre du bas se retrouve donc dans
  // son remplissage, pas dans une marge sombre. Sans fournisseur (tests), aucune marge.
  const basSur = useContext(SafeAreaInsetsContext)?.bottom ?? 0;

  const authNoticeKey = useUserStore((state) => state.authNoticeKey);
  const setAuthNoticeKey = useUserStore((state) => state.setAuthNoticeKey);
  // Notice posée hors écran (deep link de confirmation en échec) → affichée via le rendu d'erreur
  // existant, puis consommée pour ne pas la ré-afficher.
  useEffect(() => {
    if (authNoticeKey) {
      setError(authErrorText(authNoticeKey));
      setAuthNoticeKey(null);
    }
  }, [authNoticeKey, setAuthNoticeKey]);

  const isSignup = mode === 'signup';
  // `copy` et non `t` : la fonction de traduction porte déjà ce nom, et la masquer ici
  // rendait tous les autres appels invisibles.
  const copy: AuthCopy = oubli
    ? {
        title: t('auth.forgot.title'),
        titleAccent: t('auth.forgot.titleAccent'),
        subtitle: t('auth.forgot.subtitle'),
        cta: t('auth.forgot.cta'),
      }
    : mode === 'signup' && profil && profil in COPIES_PROFIL
      ? {
          ...texts().signup,
          title: t(`${COPIES_PROFIL[profil as keyof typeof COPIES_PROFIL]}.title`),
          titleAccent: t(`${COPIES_PROFIL[profil as keyof typeof COPIES_PROFIL]}.titleAccent`),
          subtitle: t(`${COPIES_PROFIL[profil as keyof typeof COPIES_PROFIL]}.subtitle`),
        }
      : texts()[mode];
  // À l'inscription, tout accès au compte exige l'acceptation des CGU (case obligatoire).
  const consentBlocked = isSignup && !termsChecked;

  const submit = async () => {
    setError(null);
    setInfo(null);
    if (consentBlocked) return;
    if (!isValidEmail(email)) {
      setError(t('auth.invalidEmail'));
      return;
    }
    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      setError(passwordCheck.reason ?? t('auth.invalidPassword'));
      return;
    }

    setLoading(true);
    try {
      const action = isSignup ? signUpEmail : signInEmail;
      const result = await action(email, password);
      if (result.errorKey) setError(authErrorText(result.errorKey));
      else if (result.pendingConfirmation) setInfo(signupPendingMessage());
    } finally {
      setLoading(false);
    }
  };

  // Le message de succès est le MÊME que le compte existe ou non : `requestPasswordReset` ne dit
  // rien de plus, et l'écran ne doit pas non plus (anti-énumération, comme `auth.pending`).
  const submitOubli = async () => {
    // Deux demandes rapprochées : la seconde reçoit « trop de demandes » et effacerait la
    // confirmation d'envoi de la première, alors que l'e-mail est bien parti.
    if (loading) return;
    setError(null);
    setInfo(null);
    const adresse = email.trim();
    if (!isValidEmail(adresse)) {
      setError(t('auth.invalidEmail'));
      return;
    }
    setLoading(true);
    try {
      const result = await requestPasswordReset(adresse);
      if (result.errorKey) setError(authErrorText(result.errorKey));
      else setInfo(t('auth.forgot.sent'));
    } finally {
      setLoading(false);
    }
  };

  const ouvrirOubli = () => {
    setError(null);
    setInfo(null);
    setOubli(true);
  };

  const fermerOubli = () => {
    setError(null);
    setInfo(null);
    setOubli(false);
  };

  const handleGoogle = async () => {
    setError(null);
    setInfo(null);
    if (consentBlocked) return;
    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.errorKey) setError(authErrorText(result.errorKey));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleApple = async () => {
    setError(null);
    setInfo(null);
    if (consentBlocked) return;
    setAppleLoading(true);
    try {
      const result = await signInWithApple();
      if (result.errorKey) setError(authErrorText(result.errorKey));
    } finally {
      setAppleLoading(false);
    }
  };

  const changeMode = (next: string) => {
    setError(null);
    setInfo(null);
    setMode(next === 'signup' ? 'signup' : 'signin');
  };

  const { large } = useGabarit();
  const titreStyle = large ? { ...styles.titre, ...styles.titreL } : styles.titre;
  const sEntete = (
    <>
        <View style={[styles.entete, colonneFormulaire]}>
          <View style={[styles.barre, large && styles.barreL]}>
            {onBack ? (
              <Pressable
                testID="auth-back"
                accessibilityRole="button"
                accessibilityLabel={t('common.back')}
                onPress={onBack}
                hitSlop={8}
                style={[styles.retour, styles.retourBouton]}
              >
                <Text style={styles.retourFleche}>{fleche('←')}</Text>
              </Pressable>
            ) : (
              <View style={styles.retour} />
            )}
            <View style={styles.marque}>
              <BrandLogo variant="mark" size={28} testID="auth-logo" />
              <Text style={styles.marqueNom}>{NOM_MARQUE}</Text>
            </View>
            <View style={styles.retour} />
          </View>

          <Text style={titreStyle}>{copy.title}</Text>
          {/* Dans un conteneur à la largeur du mot : étiré, le dégradé ne montrerait que son début. */}
          <View testID="auth-titre-accent" style={styles.accent}>
            <GradientText colors={gradients.surNuit} style={titreStyle}>
              {copy.titleAccent}
            </GradientText>
          </View>
          <Text style={[styles.sousTitre, large && styles.sousTitreL]}>{copy.subtitle}</Text>
        </View>

            </>
  );
  const sFeuille = (
    <>
<View testID="auth-feuille" style={[styles.feuille, large && styles.feuilleL, { paddingBottom: spacing.xl + 6 + basSur }]}>
          <View style={[styles.formulaire, colonneFormulaire]}>
            {oubli ? null : (
              <Segmented
                testID="auth-mode"
                value={mode}
                onChange={changeMode}
                options={[
                  { key: 'signin', label: t('auth.tabSignin') },
                  { key: 'signup', label: t('auth.tabSignup') },
                ]}
              />
            )}

            {/* Champ Email. Tout le cadre donne le focus à la saisie, pas seulement sa ligne de 19 pt.
                Le cadre n'est pas un arrêt pour les lecteurs d'écran : la saisie porte déjà le libellé. */}
            <Pressable
              testID="auth-email-cadre"
              accessible={false}
              onPress={() => emailRef.current?.focus()}
              style={[styles.champ, champActif === 'email' && styles.champActif]}
            >
              <Text style={styles.etiquette} importantForAccessibility="no">
                {t('auth.emailLabel')}
              </Text>
              <TextInput
                ref={emailRef}
                testID="auth-email"
                style={styles.saisie}
                accessibilityLabel={t('auth.emailLabel')}
                placeholder={t('auth.emailPlaceholder')}
                placeholderTextColor={colors.grey400}
                autoCapitalize="none"
                autoComplete="email"
                keyboardType="email-address"
                value={email}
                onChangeText={setEmail}
                onFocus={() => setChampActif('email')}
                onBlur={() => setChampActif(null)}
              />
            </Pressable>

            {/* Champ Mot de passe + œil */}
            {oubli ? null : (
              <Pressable
                testID="auth-password-cadre"
                accessible={false}
                onPress={() => passwordRef.current?.focus()}
                style={[styles.champ, styles.champLigne, champActif === 'password' && styles.champActif]}
              >
                <View style={styles.champCorps}>
                  <Text style={styles.etiquette} importantForAccessibility="no">
                    {t('auth.passwordLabel')}
                  </Text>
                  <TextInput
                    ref={passwordRef}
                    testID="auth-password"
                    style={styles.saisie}
                    accessibilityLabel={t('auth.passwordLabel')}
                    placeholder="••••••••"
                    placeholderTextColor={colors.grey400}
                    autoCapitalize="none"
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                    secureTextEntry={!showPassword}
                    value={password}
                    onChangeText={setPassword}
                    onFocus={() => setChampActif('password')}
                    onBlur={() => setChampActif(null)}
                  />
                </View>
                <Pressable
                  testID="auth-password-toggle"
                  accessibilityRole="button"
                  accessibilityLabel={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  onPress={() => setShowPassword((v) => !v)}
                  style={styles.oeil}
                >
                  {showPassword ? (
                    <EyeOff size={20} color={colors.grey500} strokeWidth={2} />
                  ) : (
                    <Eye size={20} color={colors.grey500} strokeWidth={2} />
                )}
              </Pressable>
            </Pressable>
            )}

            {/* Seulement à la connexion : à l'inscription le compte n'existe pas encore. */}
            {!oubli && !isSignup ? (
              <Pressable
                testID="auth-forgot-link"
                accessibilityRole="button"
                onPress={ouvrirOubli}
                hitSlop={8}
                style={styles.lien}
              >
                <Text style={styles.lienTexte}>{t('auth.forgot.link')}</Text>
              </Pressable>
            ) : null}

            {error ? (
              <Text testID="auth-error" accessibilityRole="alert" accessibilityLiveRegion="polite" style={styles.error}>
                {error}
              </Text>
            ) : null}

            {info ? (
              <Text testID="auth-info" accessibilityLiveRegion="polite" style={styles.info}>
                {info}
              </Text>
            ) : null}

            {isSignup && !oubli ? (
              <View style={styles.consent}>
                <TermsConsent
                  testID="auth-terms"
                  checked={termsChecked}
                  onToggle={setTermsChecked}
                  onOpenTerms={() => onOpenTerms?.()}
                  onOpenPrivacy={() => onOpenPrivacy?.()}
                />
              </View>
            ) : null}

            <GradientButton
              label={copy.cta}
              withArrow
              variante="cta"
              hauteur="lg"
              loading={loading}
              disabled={consentBlocked && !oubli}
              onPress={oubli ? submitOubli : submit}
              testID="auth-submit"
            />

            {oubli ? (
              <Pressable
                testID="auth-forgot-back"
                accessibilityRole="button"
                onPress={fermerOubli}
                hitSlop={8}
                style={styles.lien}
              >
                <Text style={styles.lienTexte}>{t('auth.forgot.back')}</Text>
              </Pressable>
            ) : null}

            {oubli ? null : (
            <>
              <View style={styles.dividerRow}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>{t('auth.or')}</Text>
                <View style={styles.dividerLine} />
              </View>

              <View testID="auth-sociaux" style={styles.sociaux}>
                {/* Sign in with Apple : composant natif Apple (conformité HIG garantie — logo
                    officiel, libellé localisé, rayon). Rendu iOS uniquement ; le flux signInWithApple
                    reste inchangé. Présenté AVANT Google : Apple exige une prominence au moins égale
                    aux alternatives. Le natif n'a ni prop disabled ni spinner → le wrapper coupe les
                    taps (pointerEvents) pendant le consentement manquant ou l'appel en cours. */}
                {Platform.OS === 'ios' ? (
                  <View
                    style={[styles.appleWrap, consentBlocked && styles.socialDisabled]}
                    pointerEvents={consentBlocked || appleLoading ? 'none' : 'auto'}
                  >
                    <AppleAuthentication.AppleAuthenticationButton
                      testID="auth-apple"
                      buttonType={
                        isSignup
                          ? AppleAuthentication.AppleAuthenticationButtonType.SIGN_UP
                          : AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN
                      }
                      buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.BLACK}
                      cornerRadius={formes.rayon.md}
                      style={styles.appleButton}
                      onPress={handleApple}
                    />
                  </View>
                ) : null}

                {/* Google : OAuth câblé (flux navigateur PKCE). */}
                <Pressable
                  testID="auth-google"
                  accessibilityRole="button"
                  accessibilityLabel={t('auth.google')}
                  accessibilityState={{ disabled: googleLoading || consentBlocked, busy: googleLoading }}
                  disabled={googleLoading || consentBlocked}
                  onPress={handleGoogle}
                  style={[styles.social, consentBlocked && styles.socialDisabled]}
                >
                  {googleLoading ? (
                    <ActivityIndicator testID="auth-google-spinner" color={colors.night} />
                  ) : (
                    <>
                      <GoogleLogo size={20} />
                      {/* « Continuer avec Google » ne tient pas dans un demi-bouton à 375 pt : le mot court
                          s'affiche, le libellé complet reste celui des lecteurs d'écran. */}
                      <Text style={styles.socialText}>Google</Text>
                    </>
                  )}
                </Pressable>
              </View>
            </>
          )}
          </View>
        </View>
    </>
  );

  return (
    <SafeAreaView testID="auth-screen" style={styles.container} edges={['top']}>
      <StatusBar style="light" />
      {large ? (
        <View testID="auth-split" style={styles.splitL}>
          <View style={styles.panneauMarqueL}>
            <FondNuit style={StyleSheet.absoluteFill} />
            <View style={styles.marqueContenuL}>{sEntete}</View>
          </View>
          <ScrollView
            style={styles.panneauFormL}
            contentContainerStyle={styles.panneauFormContenuL}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {sFeuille}
          </ScrollView>
        </View>
      ) : (
        <>
          <FondNuit style={styles.bandeau} />
          <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
            {sEntete}
            {sFeuille}
          </ScrollView>
        </>
      )}
    </SafeAreaView>
  );
}

const HAUTEUR_CHAMP_SOCIAL = formes.hauteur.md;

const styles = StyleSheet.create({
  splitL: { flex: 1, flexDirection: 'row', width: '100%' },
  panneauMarqueL: { flex: 1, minWidth: 0, backgroundColor: colors.night, justifyContent: 'center', overflow: 'hidden' },
  marqueContenuL: { width: '100%', maxWidth: layout.formMaxWidth + spacing.xxl * 2, alignSelf: 'center', padding: spacing.xxl },
  panneauFormL: { flex: 1, minWidth: 0, backgroundColor: colors.grey50 },
  panneauFormContenuL: { flexGrow: 1, justifyContent: 'center', padding: spacing.xxl },
  feuilleL: { flexGrow: 0, borderRadius: 0, backgroundColor: 'transparent', paddingTop: 0, paddingHorizontal: 0 },
  barreL: { justifyContent: 'flex-start', gap: spacing.lg, marginBottom: spacing.xxl * 2 },
  titreL: { fontSize: 52, lineHeight: 58, letterSpacing: -1.5 },
  sousTitreL: { fontSize: 18, lineHeight: 26, marginTop: spacing.lg },
  container: { flex: 1, backgroundColor: colors.night },
  // Halos derrière l'en-tête seulement : la feuille claire les recouvre plus bas.
  bandeau: { position: 'absolute', left: 0, right: 0, top: 0, height: HAUTEUR_BANDEAU },
  // flexGrow : la feuille s'étire jusqu'en bas de l'écran même quand son contenu est court.
  content: { flexGrow: 1 },

  entete: { paddingHorizontal: screenPadding + spacing.sm, paddingTop: spacing.sm, paddingBottom: spacing.xl },
  barre: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xl },
  retour: {
    width: formes.cible,
    height: formes.cible,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Bouton carré à voile blanc ; le même gabarit, sans fond, sert d'espaceur pour centrer la marque.
  retourBouton: {
    backgroundColor: colors.surfaceNuitForte,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
  },
  retourFleche: { fontSize: 22, lineHeight: 26, color: colors.white },
  marque: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  marqueNom: { fontSize: 19, lineHeight: 24, fontWeight: '800', color: colors.white },
  accent: { alignSelf: 'flex-start' },
  titre: { fontSize: 32, lineHeight: 38, fontWeight: '800', letterSpacing: -0.8, color: colors.white },
  sousTitre: { marginTop: 10, fontSize: 15, lineHeight: 21, color: colors.blancAtenue },

  feuille: {
    flexGrow: 1,
    backgroundColor: colors.grey50,
    borderTopLeftRadius: formes.rayon.xl,
    borderTopRightRadius: formes.rayon.xl,
    paddingTop: 22,
    paddingHorizontal: screenPadding + spacing.sm,
  },
  formulaire: { gap: 14 },

  // Champs : étiquette au-dessus de la saisie, dans un cadre blanc.
  champ: {
    gap: 6,
    paddingVertical: 11,
    paddingHorizontal: spacing.lg,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
  },
  champActif: { borderColor: colors.electric },
  champLigne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingEnd: spacing.sm },
  champCorps: { flex: 1, gap: 6 },
  etiquette: { fontSize: 12, fontWeight: '700', color: colors.grey500 },
  saisie: { padding: 0, fontSize: typography.body.size + 1, color: colors.grey900 },
  oeil: {
    width: formes.cible,
    height: formes.cible,
    borderRadius: formes.rayon.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },

  error: { color: colors.error, fontSize: typography.small.size },
  lien: { alignSelf: 'flex-end', minHeight: formes.cible, justifyContent: 'center' },
  lienTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.electricDeep },
  info: { color: colors.info, fontSize: typography.small.size, lineHeight: 18 },
  consent: { paddingVertical: spacing.xs },

  dividerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  dividerLine: { flex: 1, height: 1, backgroundColor: colors.grey200 },
  dividerText: { fontSize: typography.small.size, color: colors.grey500 },

  // Apple et Google côte à côte. Sans Apple (Android), Google prend toute la largeur.
  sociaux: { flexDirection: 'row', gap: 10 },
  social: {
    flex: 1,
    minHeight: HAUTEUR_CHAMP_SOCIAL,
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialDisabled: { opacity: 0.5 },
  socialText: { flexShrink: 1, fontSize: typography.body.size, fontWeight: '700', color: colors.night },
  // Bouton Apple natif : dimensions via style (requis), fond/rayon via buttonStyle/cornerRadius.
  appleWrap: { flex: 1, minHeight: HAUTEUR_CHAMP_SOCIAL },
  appleButton: { width: '100%', height: HAUTEUR_CHAMP_SOCIAL },
});
